import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { siteInfo, aboutSections } from '@/mocks/site';

export default function AboutPage() {
  const [activeTab, setActiveTab] = useState(aboutSections[0].id);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }
    setFormStatus('submitting');
    setFormError('');
    try {
      const res = await fetch('https://readdy.ai/api/form/d9qaih6e3oq4jqumtjfg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed: any = {};
      try { parsed = JSON.parse(text); } catch { /* ignore */ }
      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || text || '提交失败';
        setFormError(serverMsg.includes('spam') || serverMsg.includes('form data is spam') ? '提交失败，请稍后重试' : serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络后重试');
      setFormStatus('error');
    }
  };

  const activeSection = aboutSections.find((s) => s.id === activeTab) || aboutSections[0];

  return (
    <>
      <ScrollToTop />
      <header>
        <Navbar />
      </header>
      <main className="pt-16">
        <div className="bg-background-100/50 border-b border-background-200/70">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
            <div className="pb-6">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                关于我们
              </h1>
              <p className="text-foreground-600 text-sm mt-2">
                了解打屁股视频大全的品牌故事与发展历程
              </p>
            </div>
          </div>
        </div>

        <section className="py-8 md:py-12">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="flex gap-2 mb-8 overflow-x-auto">
              {aboutSections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => setActiveTab(section.id)}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    activeTab === section.id
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {section.title}
                </button>
              ))}
            </div>

            <div className="bg-background-100 rounded-lg p-6 md:p-8">
              <h2 className="font-heading text-xl font-bold text-foreground-900 mb-6">
                {activeSection.title}
              </h2>
              <div className="text-foreground-700 text-sm leading-relaxed whitespace-pre-line">
                {activeSection.content}
              </div>
            </div>

            {activeTab === 'brand-intro' && (
              <div className="mt-8 bg-primary-50 rounded-lg p-6 md:p-8">
                <h3 className="font-heading text-lg font-bold text-foreground-900 mb-4">
                  平台数据
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="text-2xl md:text-3xl font-bold text-primary-500">{siteInfo.stats.totalVideos}+</div>
                    <div className="text-foreground-500 text-xs mt-1">视频总数</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl md:text-3xl font-bold text-primary-500">{siteInfo.stats.totalCategories}</div>
                    <div className="text-foreground-500 text-xs mt-1">内容分类</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl md:text-3xl font-bold text-primary-500">{(siteInfo.stats.totalViews / 10000).toFixed(0)}万+</div>
                    <div className="text-foreground-500 text-xs mt-1">累计播放</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl md:text-3xl font-bold text-primary-500">{(siteInfo.stats.dailyActiveUsers / 10000).toFixed(1)}万+</div>
                    <div className="text-foreground-500 text-xs mt-1">日活用户</div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'contact' && (
              <div className="mt-8">
                <form
                  onSubmit={handleContactSubmit}
                  className="bg-background-100 rounded-lg p-6 md:p-8"
                  data-readdy-form=""
                >
                  <h3 className="font-heading text-lg font-bold text-foreground-900 mb-6">
                    发送消息
                  </h3>

                  {formStatus === 'success' && (
                    <div className="mb-6 p-4 bg-accent-100/80 text-accent-800 rounded-lg text-sm font-medium">
                      消息已成功发送！我们会尽快回复您。
                    </div>
                  )}

                  {formStatus === 'error' && (
                    <div className="mb-6 p-4 bg-primary-100 text-primary-700 rounded-lg text-sm font-medium">
                      {formError || '提交失败，请稍后重试'}
                    </div>
                  )}

                  <div className="honeypot-field">
                    <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        id="contact-name"
                        type="text"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        id="contact-email"
                        type="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      主题 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="contact-subject"
                      type="text"
                      name="subject"
                      required
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入消息主题"
                    />
                  </div>

                  <div className="mb-6">
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      消息内容 <span className="text-primary-500">*</span>
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                      placeholder="请输入您的消息（最多500字）"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1">最多500字</p>
                  </div>

                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full sm:w-auto px-8 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                  >
                    {formStatus === 'submitting' ? (
                      <>
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        发送中...
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line mr-2"></i>
                        发送消息
                      </>
                    )}
                  </button>
                </form>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于我们 - 打屁股视频大全',
            description: siteInfo.description,
            url: `${siteInfo.url}/about`,
            inLanguage: 'zh-CN',
            isPartOf: {
              '@type': 'WebSite',
              name: siteInfo.name,
              url: siteInfo.url,
            },
          }),
        }}
      />
    </>
  );
}